/*! bplayer.js */
import "./lib/gtm-controller";
import "./legacy/globalize";
import { version } from "build/shims";
import FacadePlayer from "./facade/facade-player";
import PlayerEvents from "./events/player-events";
import PlaylistEvents from "./events/playlist-events";
import PlaylistPlayer from "./lib/playlist/playlist-player";
import QueryString from "./utils/query-string";
import StrategyTypes from "./strategy/StrategyTypes";
import Log from "app/utils/log";
const GTM = window.BVP_GTM;

const log = new Log("BPlayer");

const BVP_LOG_QUERY_PARAM = "bvp_log_debug";

const playerStore = {};

/**
 * @class BPlayer
 * @param {?object} assetDetails BMMRAsset data
 * @param {object} userOptions
 * @param {object} callbacks
 * @return {FacadePlayer}
 */
function BPlayer(assetDetails, userOptions, callbacks) {
    return BPlayer.main(assetDetails, userOptions, callbacks);
}

/**
 * Immutable properties
 */
Object.defineProperties(BPlayer, {
    /**
     * @protected
     * @property {string} VERSION
     */
    VERSION: { value: version },

    /**
     * Expose events to public
     * @protected
     * @type {object}
     */
    events: {
        value: Object.freeze({
            PlayerEvents,
            PlaylistEvents
        })
    }
});

/**
 * @lends BPlayer
 */
Object.assign(BPlayer, {
    /**
     * Legacy interface
     * @static
     * @memberOf {BPlayer}
     * @param {?object} assetDetails BMMRAsset data
     * @param {object} originalUserOptions
     * @param {object} callbacks
     * @return {FacadePlayer}
     */
    main(assetDetails, originalUserOptions, callbacks) {
        if (!arguments.length) {
            /* eslint-disable no-console */
            if (console && console.error) {
                console.error("Attempted to initialize player without arguments");
            }
            /* eslint-enable no-console */
            return;
        }

        const userOptions = normalizeUserOptions(originalUserOptions);
        commonInit(userOptions);

        const player = new FacadePlayer(userOptions, callbacks, StrategyTypes.SCRIPT, assetDetails);

        addPlayerToStore(userOptions.htmlChildId, player);
        return player;
    },

    /**
     * Improved interface for BPlayer instantiation
     * @static
     * @memberOf {BPlayer}
     * @param {Node} containerElement
     * @param {object} originalUserOptions
     * @param {object} callbacks
     * @return {FacadePlayer}
     */
    create(containerElement, originalUserOptions = {}, callbacks) {
        const userOptions = normalizeUserOptions(originalUserOptions);
        commonInit(userOptions);

        userOptions.containerElement = containerElement;
        const player = new FacadePlayer(userOptions, callbacks, StrategyTypes.EMBED);

        addPlayerToStore(userOptions.htmlChildId, player);
        return player;
    },

    /**
     * Creates a playlist player
     * @static
     * @memberOf {BPlayer}
     * @param {Node} containerElement
     * @param {object | object[]} playlist List of user options
     * @param {object} options
     * @returns {PlaylistPlayer}
     */
    createPlaylistPlayer(containerElement, playlist, options) {
        const originalUserOptions = (Object.prototype.toString.call(playlist) === "[object Array]") ? playlist[0] : playlist;
        const userOptions = normalizeUserOptions(originalUserOptions);
        commonInit(userOptions);

        const playListPlayer = new PlaylistPlayer(containerElement, playlist, options);

        addPlayerToStore(userOptions.htmlChildId, playListPlayer);
        return playListPlayer;
    },

    /**
     * Returns cached instance of FacadePlayer
     * @static
     * @memberOf {BPlayer}
     * @param {string} htmlChildId
     * @return {?FacadePlayer}
     */
    getPlayer(htmlChildId) {
        return playerStore[htmlChildId];
    }
});

function normalizeUserOptions(userOptions) {
    if (userOptions.htmlChildId) {
        return userOptions;
    }

    return {
        ...userOptions,
        htmlChildId: `bplayer-${ Date.now() }-${ Math.floor(Math.random() * 10000) }`
    };
}

/**
 * @private
 * @param {object} userOptions
 */
function commonInit(userOptions) {
    initializeGTM(userOptions);
    initializeLogging(userOptions);
}

/**
 * @private
 * @param {string} htmlChildId
 * @param {FacadePlayer} player
 */
function addPlayerToStore(htmlChildId, player) {
    log.info("htmlChildId", htmlChildId);
    player.on(PlayerEvents.DISPOSE, removePlayerFromStore.bind(null, htmlChildId));

    playerStore[htmlChildId] = player;
}

/**
 * @private
 * @param {string} htmlChildId
 */
function removePlayerFromStore(htmlChildId) {
    playerStore[htmlChildId] = null;
}

/**
 * @private
 * @param {object} userOptions
 */
function initializeGTM(userOptions) {
    GTM.initGTM(userOptions.use_google_tag_manager, userOptions.google_tag_manager_data_layer_id);
}

/**
 * @private
 * @param {object} userOptions
 */
function initializeLogging(userOptions) {
    if (!userOptions.log_debug) {
        const pageUrl = new URL(window.location);
        const params = QueryString.parseQueryString(pageUrl.search);
        userOptions["log_debug"] = params && params[BVP_LOG_QUERY_PARAM] === "true";
    }

    const logLevel = userOptions.log_debug ? Log.getLogLevels().TRACE : Log.getLogLevels().ERROR;
    log.setMinLogLevel(logLevel);
}

/**
 * Global event that announces availability if BPlayer
 * @type {CustomEvent}
 */
setTimeout(function() {
    const bplayerReadyEvent = new CustomEvent("BPlayerLoaded", {
        detail: { bplayer: BPlayer, version },
        bubbles: true
    });
    document.dispatchEvent(bplayerReadyEvent);
});

window.BPlayer = window.BPlayer || BPlayer;
module.exports = BPlayer;
